import { AccuracySupplementalTypeEnum } from "@app/projects/enumerators";
import { AccuracyInspectionSupplement, Insurance } from "@app/projects/results/accuracy";
import { formatUnconvertedDateCustom } from "@app/shared";
import { wafflesKeyColumnName } from "@app/shared/waffles";
import { keyBy, merge, sortBy, values } from "lodash";
import generateDefaultProviderPanelRow from "./defaultProviderPanelRow";
import InspectionProfileTableData from "./inspectionProfileTableData";
import { getSupplementalDataDecoration, valueOrDashes } from "./utils";
import { JSX } from "react";

interface SortedInsurance extends Insurance {
  planName: string,
  sourceDate: Date,
  sourceName: string,
  supplementalType: AccuracySupplementalTypeEnum
  value: Insurance
}

// have to disable sort-keys here until we can change how the table data is generated on the panel.

/* eslint-disable sort-keys */
export const populateInsurance = (fieldLabel: string, keyPreface: string, tableData: InspectionProfileTableData[], providerInsurance: Insurance[], supplementInsurance: Array<AccuracyInspectionSupplement<Insurance>>, hasLimitations = false): void => {
  if (providerInsurance?.length || supplementInsurance?.length) {
    joinInsurances(providerInsurance, supplementInsurance).forEach((insurance: SortedInsurance, index: number) => {
      tableData.push({
        [wafflesKeyColumnName]: `${keyPreface}-${index}`,
        title: index === 0 ? fieldLabel : "",
        source: valueOrDashes(insurance.planName),
        result: (
          getSupplementalDataDecoration(
            getPlanNameWithLimitations(insurance, hasLimitations),
            insurance.supplementalType, null,
          )
        ),
        reference: (
          (insurance.sourceName && insurance.sourceDate) ?
            <span>
              {valueOrDashes(insurance.sourceName)}
              &nbsp;
              {valueOrDashes(insurance.sourceDate ? `(${formatUnconvertedDateCustom(insurance.sourceDate, "L")})` : "")}
            </span>
            : <span>{"---"}</span>)
      })
    })
  } else {
    tableData.push(
      generateDefaultProviderPanelRow(keyPreface, fieldLabel, null, null)
    )
  }
}

const getPlanNameWithLimitations = (insurance: SortedInsurance, hasLimitations: boolean): JSX.Element => {
  return hasLimitations
        ? (<span>{valueOrDashes(generateInsuranceWithLimitations(insurance.value))}</span>)
        : (<span>{valueOrDashes(insurance.value?.planName)}</span>);
}

export const joinInsurances = (insurances: Insurance[], supplements: Array<AccuracyInspectionSupplement<Insurance>>): SortedInsurance[] => {
  const filteredSupplements = supplements.filter(supp => supp.supplementalType != "unknown");
  const merged = merge(
    keyBy(insurances, i => i.planName.toLowerCase()),
    keyBy(filteredSupplements, s => s.value?.planName.toLowerCase()));
  const sorted = sortBy(merged, [((ins) => ins && ins.planName ? ins.planName : null), ((ins) => ins && ins.value && ins.value.planName ? ins.value.planName : null)]);
  return values(sorted);
}

function generateInsuranceWithLimitations(insurance: Insurance) {
  return insurance?.limitations && insurance.limitations.length > 0 ? `${insurance.planName}:${insurance.limitations[0]}` : insurance?.planName;
}